"use client";
import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "./Modal";
import { ActionButton } from "../buttons/ActionButton";
import { Timeframe } from "../../lib/enums/timeframe.enum";
import { fetchFromBackend } from "../../lib/utils/api";

interface EditTickerModalProps {
  isVisible: boolean;
  onClose: () => void;
  onActivate: (id: number) => Promise<boolean>;
  onDeactivate: (id: number) => Promise<boolean>;
  onDeletePair?: (id: number) => Promise<boolean>;
  onDeleteTicker?: (symbol: string) => Promise<boolean>;
  loadingTickers?: boolean;
  onOpenAddTicker?: () => void;
  onAddPair?: (tickerData: {
    symbol: string;
    pair: string;
    exchange: string;
    candleSyncTimeframes: Timeframe[];
  }) => Promise<boolean>;
  tickers: string[];
  timeframes?: Timeframe[]; // available timeframes to add
  exchanges?: string[]; // available exchanges to add
  currentPair?: string; // preselect this pair when adding
}

interface Pair {
  id: number;
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  active: boolean;
}

interface Ticker {
  symbol: string;
  pairs: Pair[];
}

export const EditTickerModal: React.FC<EditTickerModalProps> = ({
  isVisible,
  onClose,
  onActivate,
  onDeactivate,
  onDeletePair,
  onDeleteTicker,
  loadingTickers = false,
  onOpenAddTicker,
  onAddPair,
  tickers,
  timeframes = Object.values(Timeframe) as Timeframe[],
  exchanges = [],
  currentPair,
}) => {
  const [selectedTicker, setSelectedTicker] = useState<string>("");
  const [tickerData, setTickerData] = useState<Ticker | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    text: string;
    isError: boolean;
  } | null>(null);
  // no fold/unfold; show a flat list
  // Add new exchange/timeframes for existing pair within Edit
  const [newSelectedPair, setNewSelectedPair] = useState("");
  const [newExchange, setNewExchange] = useState("");
  const [newSelectedTimeframes, setNewSelectedTimeframes] = useState<
    Timeframe[]
  >([]);

  const [editingPairId, setEditingPairId] = useState<number | null>(null);
  const [editExchangeValue, setEditExchangeValue] = useState("");

  const showMessage = useCallback((text: string, isError: boolean) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  const fetchTickerDetails = useCallback(
    async (symbol: string) => {
      if (!symbol) return;
      setLoading(true);
      try {
        const data = await fetchFromBackend<Ticker>(`/ticker/${symbol}`);
        setTickerData(data);
      } catch {
        showMessage("Failed to fetch ticker details", true);
      } finally {
        setLoading(false);
      }
    },
    [showMessage]
  );

  useEffect(() => {
    if (selectedTicker) {
      void fetchTickerDetails(selectedTicker);
    }
  }, [selectedTicker, fetchTickerDetails]);

  const handleToggleActive = async (pair: Pair) => {
    const success = await (pair.active
      ? onDeactivate(pair.id)
      : onActivate(pair.id));
    if (success) {
      showMessage(
        `Pair ${pair.pair} ${
          pair.active ? "deactivated" : "activated"
        } successfully`,
        false
      );
      await fetchTickerDetails(selectedTicker);
    } else {
      showMessage("Failed to update pair status", true);
    }
  };

  const handleUpdateExchange = async (pairId: number) => {
    if (!editExchangeValue) return;
    try {
      await fetchFromBackend(`/pair/update-exchange/${pairId}`, {
        method: "PUT",
        body: JSON.stringify({ exchange: editExchangeValue }),
      });
      showMessage("Exchange updated", false);
      setEditingPairId(null);
      await fetchTickerDetails(selectedTicker);
    } catch {
      showMessage("Failed to update exchange", true);
    }
  };

  // helper: get existing timeframes for currentPair + selected exchange
  const existingForExchange = (pairName: string, exchange: string) =>
    new Set(
      (tickerData?.pairs || [])
        .filter((p) => p.pair === pairName && p.exchange === exchange)
        .map((p) => p.timeframe)
    );

  const handleNewTfToggle = (tf: Timeframe) => {
    setNewSelectedTimeframes((prev) =>
      prev.includes(tf) ? prev.filter((t) => t !== tf) : [...prev, tf]
    );
  };

  const handleAddNewPair = async () => {
    if (!tickerData) return;
    if (
      !newSelectedPair ||
      !newExchange ||
      newSelectedTimeframes.length === 0
    ) {
      showMessage("Select pair, exchange and at least one timeframe", true);
      return;
    }

    if (!onAddPair) {
      return;
    }

    try {
      const success = await onAddPair({
        symbol: tickerData.symbol,
        pair: newSelectedPair,
        exchange: newExchange,
        candleSyncTimeframes: newSelectedTimeframes,
      });

      if (success) {
        showMessage(`Added ${newSelectedPair} on ${newExchange}`, false);
        setNewSelectedPair("");
        setNewExchange("");
        setNewSelectedTimeframes([]);
        await fetchTickerDetails(selectedTicker);
      } else {
        showMessage("Failed to add new pair", true);
      }
    } catch {
      showMessage("Failed to add new pair", true);
    }
  };

  // Preselect currentPair when opening
  useEffect(() => {
    if (isVisible && currentPair) {
      setNewSelectedPair(currentPair);
    }
  }, [isVisible, currentPair]);

  // When a ticker is selected/loaded, default the pair used in the add section
  useEffect(() => {
    if (!tickerData) return;
    // Prefer currentPair if it exists for this ticker, otherwise pick first available pair
    const pairs = Array.from(
      new Set((tickerData.pairs || []).map((p) => p.pair))
    );
    if (!pairs || pairs.length === 0) return;
    if (currentPair && pairs.includes(currentPair)) {
      setNewSelectedPair(currentPair);
    } else {
      setNewSelectedPair(pairs[0]);
    }
  }, [tickerData, currentPair]);

  return (
    <Modal isVisible={isVisible} onClose={onClose} title="Manage Ticker">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <select
            value={selectedTicker}
            onChange={(e) => setSelectedTicker(e.target.value)}
            className="w-full p-2 rounded bg-slate-700 text-white border border-slate-600"
          >
            <option value="">Select a ticker</option>
            {tickers.map((ticker) => (
              <option key={ticker} value={ticker}>
                {ticker}
              </option>
            ))}
          </select>
          {loadingTickers && (
            <span className="text-xs text-gray-400">Loading…</span>
          )}
          {onOpenAddTicker && (
            <button
              type="button"
              onClick={onOpenAddTicker}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-blue-600 bg-blue-600 text-white transition hover:bg-blue-700"
              aria-label="Add new ticker"
              title="Add new ticker"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 5v14m7-7H5"
                />
              </svg>
            </button>
          )}
          {selectedTicker && onDeleteTicker && (
            <button
              type="button"
              onClick={() => {
                if (!tickerData) return;
                const confirmed = window.confirm(
                  `Delete ${tickerData.symbol} and all pairs/candles?`
                );
                if (!confirmed) return;
                void onDeleteTicker(tickerData.symbol).then((success) => {
                  if (success) {
                    showMessage("Ticker deleted", false);
                    setSelectedTicker("");
                    setTickerData(null);
                  } else {
                    showMessage("Failed to delete ticker", true);
                  }
                });
              }}
              className="h-10 rounded-md border border-red-600 bg-red-600 px-3 text-sm text-white transition hover:bg-red-700"
            >
              Delete
            </button>
          )}
        </div>

        {loading && (
          <div className="text-center">
            <div className="spinner">Loading...</div>
          </div>
        )}

        {tickerData && (
          <div className="overflow-x-auto">
            {/* List existing entries flat */}
            <table className="w-full text-white mb-3">
              <thead>
                <tr className="bg-slate-700">
                  <th className="p-2">Pair</th>
                  <th className="p-2">Exchange</th>
                  <th className="p-2">Timeframe</th>
                  <th className="p-2">Active</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickerData.pairs
                  .sort((a, b) =>
                    `${a.pair}-${a.exchange}-${a.timeframe}`.localeCompare(
                      `${b.pair}-${b.exchange}-${b.timeframe}`
                    )
                  )
                  .map((row) => (
                    <tr key={row.id} className="border-t border-slate-700">
                      <td
                        className="p-2 cursor-pointer hover:text-blue-300"
                        onClick={() => {
                          setNewSelectedPair(row.pair);
                          // Reset exchange/timeframes selection when switching pair context
                          setNewExchange("");
                          setNewSelectedTimeframes([]);
                        }}
                      >
                        {row.pair}
                      </td>
                      <td className="p-2">
                        {editingPairId === row.id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={editExchangeValue}
                              onChange={(e) =>
                                setEditExchangeValue(e.target.value)
                              }
                              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {exchanges.map((ex) => (
                                <option key={ex} value={ex}>
                                  {ex}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleUpdateExchange(row.id);
                              }}
                              className="text-green-400 hover:text-green-300"
                            >
                              ✓
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPairId(null);
                              }}
                              className="text-red-400 hover:text-red-300"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            <span
                              className="cursor-pointer hover:text-blue-300"
                              onClick={() => {
                                setNewSelectedPair(row.pair);
                                setNewExchange(row.exchange);
                                setNewSelectedTimeframes([]);
                              }}
                            >
                              {row.exchange}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPairId(row.id);
                                setEditExchangeValue(row.exchange);
                              }}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-opacity"
                              title="Edit Exchange"
                            >
                              ✎
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="p-2">{row.timeframe}</td>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={row.active}
                          onChange={() => {
                            void handleToggleActive(row);
                          }}
                          className="form-checkbox h-5 w-5 text-blue-600"
                        />
                      </td>
                      <td className="p-2">
                        {onDeletePair && (
                          <button
                            onClick={() => {
                              const confirmed = window.confirm(
                                `Delete ${row.pair} ${row.exchange} ${row.timeframe} and its candles?`
                              );
                              if (!confirmed) return;
                              void onDeletePair(row.id).then((success) => {
                                if (success) {
                                  showMessage("Pair deleted", false);
                                  void fetchTickerDetails(selectedTicker);
                                } else {
                                  showMessage("Failed to delete pair", true);
                                }
                              });
                            }}
                            className="text-red-400 hover:text-red-300"
                            title="Delete pair"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {onAddPair && (
              <div className="mt-4 p-3 border border-slate-700 rounded">
                <div className="text-white font-medium mb-2">
                  Add timeframes for pair:{" "}
                  <span className="text-blue-300">
                    {newSelectedPair || currentPair || "(select a row above)"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <select
                    value={newExchange}
                    onChange={(e) => setNewExchange(e.target.value)}
                    className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white"
                  >
                    <option value="">Select exchange</option>
                    {exchanges.map((ex) => (
                      <option key={`ex-${ex}`} value={ex}>
                        {ex}
                      </option>
                    ))}
                  </select>
                  <div className="text-gray-300 self-center">
                    Select timeframes:
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-2 mb-2">
                  {(newExchange
                    ? timeframes.filter(
                        (tf) =>
                          !existingForExchange(
                            newSelectedPair || currentPair || "",
                            newExchange
                          ).has(tf)
                      )
                    : []
                  ).map((tf) => (
                    <label
                      key={`new-${tf}`}
                      className="inline-flex items-center text-white"
                    >
                      <input
                        type="checkbox"
                        checked={newSelectedTimeframes.includes(tf)}
                        onChange={() => handleNewTfToggle(tf)}
                        className="rounded bg-slate-700 border-slate-600 text-blue-600"
                      />
                      <span className="ml-2">{tf}</span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end">
                  <ActionButton
                    onClick={() => {
                      void handleAddNewPair();
                    }}
                    label="Add Timeframes"
                    variant="primary"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-3">
          <ActionButton onClick={onClose} label="Close" variant="secondary" />
        </div>
      </div>

      {message && (
        <div
          className={`fixed top-16 p-4 rounded-md ${
            message.isError ? "bg-red-700" : "bg-green-700"
          } text-white`}
        >
          {message.text}
        </div>
      )}
    </Modal>
  );
};
